import React from 'react'
import {toast} from 'react-toastify'
import {
  CardNumberElement,
  CardMonthElement,
  CardYearElement,
  CardCvvElement,
  Elements,
} from '@recurly/react-recurly'
import Icon from '../../core/shared/Icon'
import {Tooltip} from '../../core/shared/Tooltip'
import {Input} from '../../core/shared/Input'
import {Select} from '../../core/shared/Select'
import {TaxesPromptContainer} from '../ui/payment-information'
import {useUserFeatures} from '../../context/user-features-context'
import {AutopayTerms} from './autopay-terms'
import {useBillingInfo} from './contexts/billing-info-context'
import {usePaymentAddress} from './contexts/payment-address-context'
import {usePaymentCheckboxes} from './contexts/payment-checkboxes-context'
// import {useViewport} from '../../utils/useViewport'
import {useRecurlyPayment} from './contexts/recurly-payment-context'
import {usePaymentCard} from './contexts/payment-card-context'
import {COUNTRIES} from './constants'
import {CardOnFileForm, ChangeToCardOnFileButton} from './card-on-file-form'
import {usePaymentOrder} from './hooks/use-payment-order'
import {useSelector} from 'react-redux'
import {selectedMobileViewEnabledStatus} from '../../app-store/kpGlobalStateSlice'
import {fetchSelectedCountryRegionList} from '../../utils/address-validation-util'
import '../../styles/formScreensStyles.css'
import {VisaLogo} from '../../assets/VisaLogo'
import {mastercardLogo, amexLogo, discoverLogo} from '../../assets'
import {GenericCardIcon} from '../../assets/GenericCardIcon'
import {CardValidIcon} from '../../assets/CardValidIcon'

const RecurlyPaymentForm = ({
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection,
  showSecondSection,
  nextStep,
  setName,
  businessAddress,
  country,
  setIsAddressUpdated,
  bizphone,
  isAddressUpdated,
  setPaymentRequestState,
  setCurrentStep,
  disclaimerText,
}) => {
  const {is5Order} = usePaymentOrder()
  const isMobile = useSelector(selectedMobileViewEnabledStatus)

  const {billingInfo, tryCardOnFile, isCVCVerified} = useBillingInfo()
  const [selectedCountry, setSelectedCountry] = React.useState(
    COUNTRIES.find(({value}) => value === country),
  )
  const countrySelectDisabled = true
  const {
    addressInformation,
    updateAddressField,
    resetAddress,
    setFromBusinessAddress,
  } = usePaymentAddress()
  const {
    clearRecurlyToken,
    fetchToken,
    recurlyToken,
    type,
    recurlyFetchError,
    clearRecurlyFetchError,
  } = useRecurlyPayment()
  const {updateValidation, cardValidation} = usePaymentCard()
  const {checkBoxesValues, setCheckBoxesValues} = usePaymentCheckboxes()
  const [listOfStates, setListOfStates] = React.useState([])
  const [cardBrand, setCardBrand] = React.useState(null)
  const formRef = React.useRef()

  const {useBusinessAddressInfo} = checkBoxesValues

  const {Updated_Terms: frontendUpdatedTerms} = useUserFeatures()

  React.useEffect(() => {
    setSelectedCountry(COUNTRIES.find(({value}) => value === country))
  }, [country])

  const handleOnchange = ({target: {value, name}}) => {
    setCheckBoxesValues({
      ...checkBoxesValues,
      agreedTerms: false,
    })
    clearRecurlyToken()
    updateAddressField(name, value)
    if (!isMobile) setIsAddressUpdated(false)
  }

  React.useEffect(() => {
    const getStates = () => {
      let stateLabel = ''
      if (['US', 'AU', 'NZ', 'IN'].includes(selectedCountry?.value)) {
        stateLabel = 'State'
      } else if (selectedCountry?.value === 'KY') {
        stateLabel = 'City'
      } else if (selectedCountry?.value === 'CA') {
        stateLabel = 'Province'
      } else if (selectedCountry?.value === 'BB') {
        stateLabel = 'Parish'
      } else if (selectedCountry?.value === 'GB') {
        stateLabel = 'County'
      } else if (
        ['ES', 'IT', 'PH', 'VN', 'GR'].includes(selectedCountry?.value)
      ) {
        stateLabel = 'Province'
      }
      let blankOption = [{name: 'Select ' + stateLabel, value: ''}]
      const data = fetchSelectedCountryRegionList(selectedCountry?.value)
      setListOfStates([...blankOption, ...data])
    }
    if (selectedCountry || selectedCountry?.value) {
      getStates(selectedCountry?.value || selectedCountry)
    }
  }, [selectedCountry])

  React.useEffect(() => {
    if (type !== 'card') return undefined
    if (useBusinessAddressInfo) {
      if (checkBoxesValues.updateBillingAddress) {
        setCheckBoxesValues({...checkBoxesValues, updateBillingAddress: false})
      }
      setFromBusinessAddress(businessAddress)
    } else {
      if (isMobile) {
        updateAddressField('address', '')
        updateAddressField('city', '')
        updateAddressField('firstName', '')
        updateAddressField('lastName', '')
        updateAddressField('state', '')
      } else {
        resetAddress()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBusinessAddressInfo, businessAddress, listOfStates, type, isMobile])

  React.useEffect(() => {
    if (type !== 'card') return null
    if (!checkBoxesValues.agreedTerms) return null
    if (recurlyToken) return null
    if (billingInfo && tryCardOnFile) return null
    fetchToken(formRef.current).catch(error => {
      setCurrentStep?.(2)
      setCheckBoxesValues({
        ...checkBoxesValues,
        agreedTerms: false,
      })
    })
  }, [
    recurlyToken,
    checkBoxesValues.agreedTerms,
    billingInfo,
    tryCardOnFile,
    type,
  ])

  React.useEffect(() => {
    if (!isCVCVerified) return null
    if (isMobile) return null
    setCurrentStep?.(4)
  }, [isCVCVerified])

  if (billingInfo && tryCardOnFile) {
    return (
      <CardOnFileForm
        setCalculatingTaxes={setCalculatingTaxes}
        setName={setName}
        setIsAddressUpdated={setIsAddressUpdated}
        calculatingTaxes={calculatingTaxes}
        setPaymentRequestState={setPaymentRequestState}
        disclaimerText={disclaimerText}
      />
    )
  }

  return (
    <form
      ref={formRef}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* ENTER CARD DETAILS */}
      <div style={{position: 'relative'}}>
        {checkBoxesValues.agreedTerms && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              cursor: 'not-allowed',
              borderRadius: '10px',
            }}
            onClick={() =>
              toast.warn(
                'Please uncheck the Terms and Conditions to change your payment information.',
              )
            }
          />
        )}
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: isMobile ? '10px' : '16px',
            opacity: checkBoxesValues.agreedTerms ? 0.6 : 1,
            pointerEvents: checkBoxesValues.agreedTerms ? 'none' : 'auto',
          }}
        >
          <div className="section-heading">
            <div className="section-heading-title-container">
              <div
                className="d-flex align-items-center"
                style={{columnGap: '8px'}}
              >
                <Tooltip
                  variant="bottom"
                  description="Provide card details to proceed with payment"
                  width="220px"
                >
                  <div
                    className="d-flex align-items-center"
                    style={{columnGap: '4px'}}
                  >
                    <p className="section-heading-title">Enter Card Details</p>
                    <Icon name="info" color="#808080" size="xxs" />
                  </div>
                </Tooltip>
              </div>
            </div>
            <ChangeToCardOnFileButton />
          </div>
          <div className="card-name-row">
            <Input
              variant="payment"
              label="First Name"
              data-recurly="first_name"
              placeholder="Cardholder First Name"
              name="firstName"
              onChange={handleOnchange}
              value={addressInformation.firstName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
            <Input
              variant="payment"
              label="Last Name"
              data-recurly="last_name"
              placeholder="Cardholder Last Name"
              name="lastName"
              onChange={handleOnchange}
              value={addressInformation.lastName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
          </div>
          <div className="form-field-container">
            <div className="card-details-grid" style={{gridColumn: 'span 5'}}>
              {/* Card Number */}
              <div
                style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
              >
                <Tooltip
                  variant="top"
                  description="Enter the 16-digit card number on the card"
                  width="220px"
                  className="align-self-start"
                >
                  <label className="payment-variant-label">
                    Enter Valid Card Number
                  </label>
                </Tooltip>
                <Tooltip
                  variant="top"
                  description={
                    checkBoxesValues.agreedTerms
                      ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                      : 'Enter the 16-digit card number on the card'
                  }
                  width="220px"
                  block
                  suppressNativeTitle
                >
                  <div className="card-number-wrapper">
                    <div className="card-brand-slot">
                      {cardBrand === 'visa' && <VisaLogo width={30} />}
                      {cardBrand === 'master' && (
                        <img
                          src={mastercardLogo}
                          alt="Mastercard logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {cardBrand === 'american_express' && (
                        <img
                          src={amexLogo}
                          alt="American Express logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {cardBrand === 'discover' && (
                        <img
                          src={discoverLogo}
                          alt="Discover logo"
                          style={{width: '30px', objectFit: 'contain'}}
                        />
                      )}
                      {(!cardBrand || cardBrand === 'unknown') && (
                        <GenericCardIcon />
                      )}
                    </div>
                    <CardNumberElement
                      onChange={e => {
                        setCardBrand(e.brand)
                        updateValidation('cardNumber', e.valid)
                        clearRecurlyToken()
                      }}
                      className="card-number-input"
                      style={{
                        fontFamily: 'Open Sans',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#000000',
                        placeholder: {color: '#9ca3af', content: 'Card number'},
                      }}
                      disabled={checkBoxesValues.agreedTerms}
                    />
                    {cardValidation.cardNumber && (
                      <div className="card-valid-slot">
                        <CardValidIcon />
                      </div>
                    )}
                  </div>
                </Tooltip>
              </div>

              {/* Expiry Date + CVV */}
              <div className="card-details-grid">
                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
                >
                  <Tooltip
                    variant="top"
                    description="Enter the card's expiration date"
                    width="220px"
                    className="align-self-start"
                  >
                    <label className="payment-variant-label">Expiry Date</label>
                  </Tooltip>
                  <Tooltip
                    variant="top"
                    description={
                      checkBoxesValues.agreedTerms
                        ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                        : "Enter the card's expiration date"
                    }
                    width="220px"
                    block
                    suppressNativeTitle
                  >
                    <div className="card-expiry-group">
                      <CardMonthElement
                        onChange={e => {
                          updateValidation('cardMonth', e.valid)
                          clearRecurlyToken()
                        }}
                        className="card-info-input w-full"
                        style={{
                          fontFamily: 'Open Sans',
                          fontSize: '12px',
                          fontWeight: '400',
                          color: '#000000',
                          placeholder: {color: '#9ca3af', content: 'MM'},
                        }}
                        disabled={checkBoxesValues.agreedTerms}
                      />
                      <span className="card-expiry-separator">/</span>
                      <CardYearElement
                        onChange={e => {
                          updateValidation('cardYear', e.valid)
                          clearRecurlyToken()
                        }}
                        className="card-info-input w-full"
                        style={{
                          fontFamily: 'Open Sans',
                          fontSize: '12px',
                          fontWeight: '400',
                          color: '#000000',
                          placeholder: {color: '#9ca3af', content: 'YY'},
                        }}
                        disabled={checkBoxesValues.agreedTerms}
                      />
                    </div>
                  </Tooltip>
                </div>

                <div
                  style={{display: 'flex', flexDirection: 'column', gap: '4px'}}
                >
                  <Tooltip
                    variant="top"
                    description="Enter the 3–4 digit code"
                    width="220px"
                    className="align-self-start"
                  >
                    <label className="payment-variant-label">CVV / CVC</label>
                  </Tooltip>
                  <Tooltip
                    variant="top"
                    description={
                      checkBoxesValues.agreedTerms
                        ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                        : 'Enter the 3–4 digit code'
                    }
                    width="220px"
                    block
                    suppressNativeTitle
                  >
                    <CardCvvElement
                      onChange={e => {
                        updateValidation('cardCVV', e.valid)
                        clearRecurlyToken()
                      }}
                      className="card-info-input w-full"
                      style={{
                        fontFamily: 'Open Sans',
                        fontSize: '12px',
                        fontWeight: '400',
                        color: '#000000',
                        placeholder: {color: '#9ca3af', content: 'CVV'},
                      }}
                      disabled={checkBoxesValues.agreedTerms}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          {is5Order && frontendUpdatedTerms && <AutopayTerms />}
        </div>
      </div>

      {/* BILLING ADDRESS */}
      <div style={{position: 'relative'}}>
        {checkBoxesValues.agreedTerms && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              cursor: 'not-allowed',
              borderRadius: '10px',
            }}
            onClick={() =>
              toast.warn(
                'Please uncheck the Terms and Conditions to change your payment information.',
              )
            }
          />
        )}
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: isMobile ? '10px' : '16px',
            opacity: checkBoxesValues.agreedTerms ? 0.6 : 1,
            pointerEvents: checkBoxesValues.agreedTerms ? 'none' : 'auto',
          }}
        >
          {is5Order && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div className="section-heading section-heading--no-divider">
                <div className="section-heading-title-container">
                  <div
                    className="d-flex align-items-center"
                    style={{columnGap: '16px'}}
                  >
                    <Tooltip
                      variant="top"
                      description="Provide the billing address for this payment"
                      width="220px"
                    >
                      <div
                        className="d-flex align-items-center"
                        style={{columnGap: '4px'}}
                      >
                        <p className="section-heading-title">Billing Address</p>
                        <Icon name="info" color="#808080" size="xxs" />
                      </div>
                    </Tooltip>
                    <div
                      className="d-flex align-items-center"
                      style={{columnGap: '6px'}}
                    >
                      <input
                        type="checkbox"
                        id="useBusinessAddressInfoMobile"
                        name="useBusinessAddressInfo"
                        disabled={checkBoxesValues.agreedTerms}
                        checked={checkBoxesValues.useBusinessAddressInfo}
                        onChange={e => {
                          setCheckBoxesValues({
                            ...checkBoxesValues,
                            useBusinessAddressInfo: e.target.checked,
                            agreedTerms: false,
                          })
                          clearRecurlyToken()
                        }}
                        style={{
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px',
                          accentColor: '#5378FC',
                        }}
                      />
                      <label
                        htmlFor="useBusinessAddressInfoMobile"
                        className="business-address-label"
                        style={{
                          cursor: 'pointer',
                          marginBottom: '0px',
                        }}
                      >
                        Same as my business address
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          {is5Order && isAddressUpdated && (
            <TaxesPromptContainer>
              <Icon name="info" color="#057AFF" />
              <p className="m-0">
                The current estimated taxes are based on your business address.
              </p>
            </TaxesPromptContainer>
          )}
          {!is5Order && (
            <Select
              options={COUNTRIES.map(({name, value}) => ({label: name, value}))}
              label="Country"
              className="recurly-element recurly-address mt-3"
              data-recurly="country"
              value={selectedCountry?.value}
              onChange={e => {
                setSelectedCountry(
                  COUNTRIES.find(c => c.value === e.target.value),
                )
                clearRecurlyToken()
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={countrySelectDisabled}
              title="Country is set by the account and cannot be changed."
            />
          )}
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry?.value || selectedCountry}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
          <Input
            variant="payment"
            label="Street Address"
            type="text"
            data-recurly="address1"
            placeholder="Street Address"
            name="address"
            value={addressInformation.address}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
            style={{
              marginBottom: isMobile ? '10px' : '20px',
            }}
          />
          <div
            className={`responsive-address-container ${
              !is5Order && 'pr-1 address_container'
            }`}
            style={{marginBottom: isMobile ? '10px' : '20px'}}
          >
            <Input
              variant="payment"
              label="City"
              type="text"
              data-recurly="city"
              placeholder="City"
              name="city"
              value={addressInformation.city}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">State</label>
              <select
                className="recurly-address"
                style={{
                  height: '32px',
                  border: '1px solid #BFBFBF',
                  borderRadius: '5px',
                  fontFamily: 'Open Sans',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  paddingLeft: '8px',
                }}
                value={addressInformation?.state}
                onChange={e => {
                  handleOnchange({
                    target: {
                      name: 'state',
                      value: e.target.selectedOptions[0].value,
                    },
                  })
                  clearRecurlyToken()
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    agreedTerms: false,
                  })
                }}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                    : undefined
                }
              >
                {listOfStates.map(({name, value}) => {
                  return (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  )
                })}
              </select>
            </div>
            <input
              hidden
              readOnly
              value={addressInformation?.state}
              type="text"
              className="recurly-element"
              data-recurly="state"
              placeholder="State"
            />
            <Input
              variant="payment"
              label="Postal Code"
              type="text"
              data-recurly="postal_code"
              placeholder="Postal Code"
              name="postalCode"
              value={addressInformation.postalCode}
              onChange={e => {
                handleOnchange(e)
                clearRecurlyFetchError()
              }}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
              errorText={
                recurlyFetchError?.fields?.includes('postal_code')
                  ? 'Invalid Postal Code'
                  : undefined
              }
            />
          </div>
          {is5Order && (
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : undefined,
                gap: '16px',
                alignItems: isMobile ? 'stretch' : 'flex-start',
              }}
            >
              <div style={{flex: 1, minWidth: 0}}>
                <Select
                  options={COUNTRIES.map(({name, value}) => ({
                    label: name,
                    value,
                  }))}
                  label="Country"
                  size="sm"
                  variant="payment"
                  className="payment-country-select"
                  data-recurly="country"
                  value={selectedCountry?.value}
                  onChange={e => {
                    setSelectedCountry(
                      COUNTRIES.find(c => c.value === e.target.value),
                    )
                    clearRecurlyToken()
                    setCheckBoxesValues({
                      ...checkBoxesValues,
                      agreedTerms: false,
                    })
                  }}
                  disabled={countrySelectDisabled}
                  title="Country is set by the account and cannot be changed."
                />
              </div>
              <div
                style={{
                  flex: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingTop: isMobile ? '10px' : '16px',
                }}
              >
                {disclaimerText && (
                  <p className="card-number-helper-text" style={{margin: 0}}>
                    {disclaimerText}
                  </p>
                )}
                <p className="card-number-helper-text" style={{margin: 0}}>
                  {
                    "By processing your payment, you agree that your personal information will be shared with Thryv's payment processor—Adyen, N.V. and will be subject to Adyen's "
                  }
                  <a
                    target="_blank"
                    href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                    rel="noreferrer"
                    style={{fontWeight: 700, color: '#5378FC'}}
                  >
                    privacy statement
                  </a>
                  {'.'}
                </p>
              </div>
            </div>
          )}
          {!is5Order && (
            <div className="mt-4 mb-2">
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <input
                  type="checkbox"
                  id="useBusinessAddressInfoDesktop"
                  name="useBusinessAddressInfo"
                  disabled={checkBoxesValues.agreedTerms}
                  checked={checkBoxesValues.useBusinessAddressInfo}
                  onChange={e => {
                    setCheckBoxesValues({
                      ...checkBoxesValues,
                      useBusinessAddressInfo: e.target.checked,
                      agreedTerms: false,
                    })
                    clearRecurlyToken()
                  }}
                  title={
                    checkBoxesValues.agreedTerms
                      ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                      : undefined
                  }
                />
                <label
                  htmlFor="useBusinessAddressInfoDesktop"
                  className="business-address-label"
                >
                  Same as my business address
                </label>
              </div>
              {frontendUpdatedTerms && <AutopayTerms styles={{marginTop: 0}} />}
            </div>
          )}
        </div>
      </div>
    </form>
  )
}

const PaymentForm = ({
  nextStep,
  calculatingTaxes,
  setCalculatingTaxes,
  showFirstSection = false,
  showSecondSection = false,
  checkBoxesValues,
  setCheckBoxesValues,
  setName,
  businessAddress,
  country,
  handleCompleteOrder,
  setIsAddressUpdated,
  bizphone,
  is5Order,
  isAddressUpdated,
  paymentPreview,
  paymentRequestState,
  setPaymentRequestState,
  setCurrentStep,
  disclaimerText,
}) => {
  return (
    <Elements>
      <RecurlyPaymentForm
        calculatingTaxes={calculatingTaxes}
        setCalculatingTaxes={setCalculatingTaxes}
        showFirstSection={showFirstSection}
        showSecondSection={showSecondSection}
        nextStep={nextStep}
        checkBoxesValues={checkBoxesValues}
        setCheckBoxesValues={setCheckBoxesValues}
        setName={setName}
        businessAddress={businessAddress}
        country={country}
        handleCompleteOrder={handleCompleteOrder}
        setIsAddressUpdated={setIsAddressUpdated}
        bizphone={bizphone}
        is5Order={is5Order}
        isAddressUpdated={isAddressUpdated}
        paymentPreview={paymentPreview}
        paymentRequestState={paymentRequestState}
        setPaymentRequestState={setPaymentRequestState}
        setCurrentStep={setCurrentStep}
        disclaimerText={disclaimerText}
      />
    </Elements>
  )
}

export {PaymentForm}
